/**
 * Skill run flow dialog (web): Skills v2, type-aware.
 *
 * The configure + result bodies switch on the skill's run-spec (skill-run-spec):
 *   • CV skills   → pick a CV + "Update this CV" | "Save as new version" (named)
 *   • CV + job    → CV + a job-description box
 *   • Job         → a job-description box (no CV)
 *   • Topic       → a short topic box (posts)
 *   • Free-text   → describe your situation (coaching / interview / outreach)
 *   • Profile/none→ nothing; runs on your profile
 *
 * CV-editing skills return a before/after diff (View CV / Undo / Regenerate).
 * Text skills return markdown you can Copy / Regenerate: they never touch a CV.
 * Single real generation (the result you see IS what was written).
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
} from "react";
import { useRouter } from "expo-router";
import { useTranslation } from "react-i18next";
import type { SkillSummary } from "@tabbio/types";
import { HugeSymbol as MaterialSymbol } from "../icons/HugeSymbol";
import { NovaSparkle } from "../common/NovaSparkle";
import { WebDialog } from "../common/WebDialog.web";
import { useToast } from "../../hooks/useToast";
import { useUpgradeModal } from "../../providers/useUpgradeModal";
import { useAiActionGuard } from "../../providers/SubscriptionProvider.shared";
import { useLocale } from "../../hooks/useLocale";
import { buildCvRoute, buildMyTabbioRoute, buildTailoredCvRoute } from "../../lib/cv-target-route";
import { createUpgradeContext } from "../../lib/monetization-context";
import {
  createTailoredCvFromParsed,
  deleteTailoredCV,
  duplicateTailoredCV,
  fetchTailoredCVs,
} from "../../lib/tailored-cv-api";
import {
  buildCvListWithMainFirst,
  MASTER_CV_ID,
  type CvListItem,
} from "../../lib/cv-main-default";
import { trackProductEvent } from "../../lib/product-analytics";
import {
  ResumeImportComposer,
  type ResumeImportComposerParsed,
} from "../cv/ResumeImportComposer";
import {
  resolveRunSpec,
  runSpecFor,
  skillNeedsCvPicker,
  type SkillInputKind,
} from "./skill-run-spec";
import { resolveSkillRunCvTarget } from "./skill-run-cv-target";
import {
  getSkill,
  isConfirmationRequired,
  isEntitlementDenied,
  isConfirmationOutdated,
  isSkillBlocked,
  prepareSkillRunFor,
  runSkill,
  type RunSkillInput,
  type SkillDetailWithConfig,
  type SkillEntitlementDecision,
} from "../../lib/skills-api";
import {
  declaredInputsComplete,
  declaredInputsPayload,
  SkillDeclaredInputs,
  SkillDiffView,
  skillDiffIsEmpty,
  SkillTierBadge,
} from "./skill-ui.web";
import { skillInputFields } from "./skill-taxonomy";
import { prettySkillName } from "./skill-name";
import { undoSkillRunWithConflictPrompt } from "../../lib/skill-undo-flow";
import {
  buildSkillRunEntitlementView,
  getSkillRunActionKey,
} from "./skill-run-entitlement";

type Phase = "configure" | "running" | "result" | "error";
type ApplyMode = "update" | "new_version";

interface Props {
  skill: SkillSummary;
  /** Default CV: pre-selects the picker / used as reference for text skills. */
  cvId?: string | null;
  onClose: () => void;
  onComplete?: () => void;
}

export function SkillRunDialog({ skill, cvId, onClose, onComplete }: Props) {
  const { t } = useTranslation("common");
  const { isRTL, locale } = useLocale();
  const toast = useToast();
  const router = useRouter();
  const upgradeModal = useUpgradeModal();

  // Full detail: carries the manifest run block (better spec resolution than
  // the summary-only seed map) and the tabbio/v2 declarative `inputs[]`.
  // undefined = loading; null = fetch failed (legacy summary-based behavior).
  const [detail, setDetail] = useState<
    SkillDetailWithConfig | null | undefined
  >(undefined);
  useEffect(() => {
    let alive = true;
    setDetail(undefined);
    getSkill(skill.skillId)
      .then((d) => {
        if (alive) setDetail(d);
      })
      .catch(() => {
        if (alive) setDetail(null);
      });
    return () => {
      alive = false;
    };
  }, [skill.skillId]);
  const detailLoading = detail === undefined;

  const spec = useMemo(
    () =>
      detail
        ? resolveRunSpec(
            { run: detail.run, kind: detail.kind },
            skill.skillId,
            detail.categories ?? skill.categories,
          )
        : runSpecFor(skill.skillId, skill.categories),
    [detail, skill.skillId, skill.categories],
  );
  const kind: SkillInputKind = spec.inputKind;
  const isTextSkill = spec.outputKind === "text";
  // CV-producing skills need a concrete target even when their conceptual
  // input is the stored profile. Profile-only text skills do not.
  const needsCvPicker = skillNeedsCvPicker(spec);
  // v2 declarative run-screen fields replace the built-in jd/topic/free-text
  // boxes; a legacy object inputsSchema keeps the current behavior exactly.
  const inputFields = useMemo(
    () => skillInputFields(detail?.inputsSchema),
    [detail?.inputsSchema],
  );
  const hasDeclaredInputs = !!inputFields;
  const needsJd = !hasDeclaredInputs && (kind === "cv_jd" || kind === "job");
  const needsTopic = !hasDeclaredInputs && kind === "topic";
  const needsFreeText = !hasDeclaredInputs && kind === "text";
  const showApplyMode = spec.outputKind === "cv_edit" && needsCvPicker;

  const [phase, setPhase] = useState<Phase>("configure");
  const [cvs, setCvs] = useState<CvListItem[] | null>(null);
  const [selectedCvId, setSelectedCvId] = useState<string | null>(cvId ?? null);
  // Device CV import (issue #281): mirrors the native SkillRunSheet flow.
  const [importing, setImporting] = useState(false);
  const [creatingCv, setCreatingCv] = useState(false);
  const [applyMode, setApplyMode] = useState<ApplyMode>("update");
  const [versionName, setVersionName] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [topic, setTopic] = useState("");
  const [freeText, setFreeText] = useState("");
  /** Declarative field values, keyed by SkillInputField id. */
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});

  const [diff, setDiff] = useState<Record<string, unknown>>({});
  const [runId, setRunId] = useState<string | null>(null);
  const [resultCvId, setResultCvId] = useState<string | null>(null);
  const [undoing, setUndoing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [serverEntitlementDecision, setServerEntitlementDecision] =
    useState<SkillEntitlementDecision | null>(null);

  const skillRunGuard = useAiActionGuard(getSkillRunActionKey(skill.tier));
  const entitlementDecision =
    serverEntitlementDecision ?? skillRunGuard.decision;
  const entitlementView = useMemo(
    () =>
      buildSkillRunEntitlementView({
        decision: entitlementDecision,
        locale,
        t,
        subscriptionError: skillRunGuard.error,
      }),
    [entitlementDecision, locale, skillRunGuard.error, t],
  );

  useEffect(() => {
    setServerEntitlementDecision(null);
    setImporting(false);
    setCreatingCv(false);
  }, [skill.skillId]);

  const handleImportParsed = useCallback(
    async ({ parsed }: ResumeImportComposerParsed) => {
      setCreatingCv(true);
      try {
        const cv = await createTailoredCvFromParsed({ parsed });
        const list = await fetchTailoredCVs();
        const defaultCvId =
          list.find((item) => item.isDefaultAutoApplyCv)?.id ?? null;
        setCvs(
          buildCvListWithMainFirst(
            list,
            defaultCvId,
            t(defaultCvId ? "cv.list.profileTitle" : "cv.list.masterTitle", {
              ns: "seeker",
            }),
          ),
        );
        setSelectedCvId(cv.id);
        setImporting(false);
        trackProductEvent("cv_imported", { source: "skill_run_web" });
      } catch {
        toast.error(t("skills.runDialog.importFailed"));
      } finally {
        setCreatingCv(false);
      }
    },
    [t, toast],
  );

  useEffect(() => {
    if (!needsCvPicker && !isTextSkill) return;
    let alive = true;
    void fetchTailoredCVs()
      .then((list) => {
        if (!alive) return;
        const defaultCvId =
          list.find((item) => item.isDefaultAutoApplyCv)?.id ?? null;
        const options = buildCvListWithMainFirst(
          list,
          defaultCvId,
          t(defaultCvId ? "cv.list.profileTitle" : "cv.list.masterTitle", {
            ns: "seeker",
          }),
        );
        setCvs(options);
        setSelectedCvId((prev) => prev ?? options[0]?.id ?? null);
      })
      .catch(() => {
        if (alive) setCvs([]);
      });
    return () => {
      alive = false;
    };
  }, [needsCvPicker, isTextSkill, t]);

  const selectedCv = cvs?.find((c) => c.id === selectedCvId) ?? null;
  const outputText = String(
    (diff.outputText as string | undefined) ?? "",
  ).trim();

  const openUpgrade = useCallback(
    (decision: SkillEntitlementDecision | undefined) => {
      if (!decision) {
        upgradeModal.open({
          placement: "skill_run",
          entryPoint: "skills.run_dialog",
          planSet: "individual",
          recommendedTier: "plus",
          returnTo: `/skills/${encodeURIComponent(skill.skillId)}`,
        });
        return;
      }
      upgradeModal.open(
        createUpgradeContext({
          decision,
          placement: "skill_run",
          entryPoint: "skills.run_dialog",
          returnTo: `/skills/${encodeURIComponent(skill.skillId)}`,
        }),
      );
    },
    [skill.skillId, upgradeModal],
  );

  const canRun =
    !detailLoading &&
    entitlementView.canRun &&
    (!needsCvPicker || !!selectedCvId) &&
    (!needsJd || jobDescription.trim().length > 0) &&
    (!needsTopic || topic.trim().length > 0) &&
    (!needsFreeText || freeText.trim().length > 0) &&
    declaredInputsComplete(inputFields, fieldValues) &&
    (!showApplyMode ||
      applyMode !== "new_version" ||
      versionName.trim().length > 0);

  const handleRun = useCallback(async () => {
    if (!canRun) return;
    // CV skills write to `selectedCvId`; text skills pass the default CV as
    // reference only (backend never edits it for text output).
    const picked = needsCvPicker ? selectedCvId : (cvId ?? undefined);
    const inputs: Record<string, unknown> = inputFields
      ? declaredInputsPayload(inputFields, fieldValues)
      : {};
    if (needsJd) inputs.jobDescription = jobDescription.trim();
    if (needsTopic) inputs.topic = topic.trim();
    if (needsFreeText) inputs.request = freeText.trim();

    const target = resolveSkillRunCvTarget({ selected: picked, spec });

    setPhase("running");
    // A CV skill starting from the profile-backed Main has no row to write
    // into, and the server would silently downgrade the run to a dry run. Make
    // the copy first so the write lands; drop it again if the run fails, so a
    // failure never leaves an empty CV behind.
    let materializedCvId: string | null = null;
    try {
      if (target.materializeMaster) {
        materializedCvId = (
          await duplicateTailoredCV({
            cvId: MASTER_CV_ID,
            title: showApplyMode && applyMode === "new_version"
              ? versionName.trim() || undefined
              : undefined,
          })
        ).id;
      }
      const effectiveCvId = materializedCvId ?? target.cvId;

      const base: RunSkillInput = {
        cvId: effectiveCvId ?? undefined,
        inputs,
        dryRun: false,
      };
      if (materializedCvId) {
        // The fresh copy IS the deliverable, so the skill updates it in place
        // (a "new version" of a CV that did not exist a moment ago is noise).
        base.mode = "update";
      } else if (showApplyMode) {
        base.mode = applyMode;
        if (applyMode === "new_version") base.title = versionName.trim();
      }

      const result = await runSkill(skill.skillId, base).catch(
        async (error) => {
          if (isConfirmationRequired(error) && effectiveCvId) {
            // The confirmation covers this exact write: CV, mode and title.
            const prepared = await prepareSkillRunFor(skill.skillId, {
              ...base,
              cvId: effectiveCvId,
            });
            return runSkill(skill.skillId, {
              ...base,
              confirmationToken: prepared.confirmationToken,
            });
          }
          throw error;
        },
      );
      const nextDiff =
        result.diff ??
        (result.run?.diff as Record<string, unknown> | undefined) ??
        {};
      setDiff(nextDiff);
      setRunId(result.runId ?? result.run?.id ?? null);
      setResultCvId(result.run?.resultCvId ?? null);
      // Point the selection at the CV that was actually written. Left on the
      // `master` row, the result banner would read "Updated Main" over a run
      // that created a copy and never touched the profile-backed Main.
      if (materializedCvId) setSelectedCvId(materializedCvId);
      setPhase("result");
      trackProductEvent("skill_applied", {
        skillId: skill.skillId,
        hasCv: Boolean(effectiveCvId),
        applyMode: showApplyMode ? applyMode : null,
        producedCv: Boolean(result.run?.resultCvId),
      });
      void skillRunGuard.reconcile().catch(() => undefined);
      onComplete?.();
    } catch (error) {
      // A failed run must not leave the half-made profile copy behind.
      if (materializedCvId) {
        await deleteTailoredCV(materializedCvId).catch(() => undefined);
      }
      if (isEntitlementDenied(error)) {
        trackProductEvent("feature_blocked", {
          feature: "skill_run",
          skillId: skill.skillId,
        });
        setServerEntitlementDecision(
          error.decision ?? {
            ...entitlementDecision,
            allowed: false,
            reasonCode: "unknown",
            message: error.message,
            promptKey: "individual.skill_run",
            recommendedPlanSet: "individual",
            recommendedTier: "plus",
          },
        );
        setErrorMessage(error.message);
        setPhase("configure");
        void skillRunGuard.reconcile().catch(() => undefined);
        return;
      }
      setErrorMessage(
        isSkillBlocked(error)
          ? t("skills.runDialog.blocked")
          : isConfirmationOutdated(error)
            ? t("skills.runDialog.reviewAgain")
            : error instanceof Error
              ? error.message
              : t("skills.runDialog.failed"),
      );
      setPhase("error");
    }
  }, [
    canRun,
    needsCvPicker,
    selectedCvId,
    cvId,
    inputFields,
    fieldValues,
    needsJd,
    jobDescription,
    needsTopic,
    topic,
    needsFreeText,
    freeText,
    spec,
    showApplyMode,
    applyMode,
    versionName,
    skill.skillId,
    onComplete,
    entitlementDecision,
    skillRunGuard,
    t,
  ]);

  const handleViewCv = useCallback(() => {
    const target = resultCvId ?? (needsCvPicker ? selectedCvId : null);
    if (target) {
      router.push(
        target === MASTER_CV_ID
          ? buildCvRoute("/(app)/cv", target)
          : buildTailoredCvRoute(target),
      );
    }
    onClose();
  }, [resultCvId, needsCvPicker, selectedCvId, router, onClose]);

  const handleUndo = useCallback(async () => {
    if (!runId) return;
    setUndoing(true);
    try {
      // B05: edits made after the run are never overwritten without asking.
      const outcome = await undoSkillRunWithConflictPrompt(runId, t);
      if (outcome.kind === "kept") {
        setUndoing(false);
        return;
      }
      toast.success(t("skills.runDialog.undone"));
      onComplete?.();
      onClose();
    } catch {
      toast.error(t("skills.runDialog.undoFailed"));
      setUndoing(false);
    }
  }, [runId, toast, t, onComplete, onClose]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(outputText);
      toast.success(t("skills.runDialog.copied"));
    } catch {
      toast.error(t("skills.runDialog.copyFailed"));
    }
  }, [outputText, toast, t]);

  const emptyCvResult =
    phase === "result" &&
    !isTextSkill &&
    (skillDiffIsEmpty(diff) || !resultCvId);

  // ── Footer per phase ──────────────────────────────────────────────────────
  let footer: React.ReactNode = null;
  if (phase === "configure") {
    footer = (
      <div style={footerRow}>
        <button type="button" onClick={onClose} style={secondaryBtn}>
          {t("skills.runDialog.cancel")}
        </button>
        <button
          type="button"
          onClick={() => void handleRun()}
          disabled={!canRun}
          style={{
            ...primaryBtn,
            opacity: canRun ? 1 : 0.4,
            cursor: canRun ? "pointer" : "default",
          }}
        >
          <NovaSparkle size={18} />
          {t("skills.runDialog.run")}
        </button>
      </div>
    );
  } else if (phase === "result") {
    footer = (
      <div style={footerRow}>
        <button
          type="button"
          onClick={() => void handleRun()}
          style={secondaryBtn}
        >
          <MaterialSymbol name="refresh" size={17} />
          {t("skills.runDialog.regenerate")}
        </button>
        {isTextSkill ? (
          <button
            type="button"
            onClick={() => void handleCopy()}
            style={primaryBtn}
            disabled={!outputText}
          >
            <MaterialSymbol name="content_copy" size={17} />
            {t("skills.runDialog.copy")}
          </button>
        ) : emptyCvResult ? (
          <button type="button" onClick={onClose} style={primaryBtn}>
            {t("skills.runDialog.doneButton")}
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={() => void handleUndo()}
              disabled={undoing || !runId}
              style={{ ...secondaryBtn, opacity: undoing ? 0.5 : 1 }}
            >
              {undoing
                ? t("skills.runDialog.undoing")
                : t("skills.runDialog.undo")}
            </button>
            <button type="button" onClick={handleViewCv} style={primaryBtn}>
              {t("skills.runDialog.viewCv")}
              <MaterialSymbol
                name={isRTL ? "arrow_back" : "arrow_forward"}
                size={18}
              />
            </button>
          </>
        )}
      </div>
    );
  } else if (phase === "error") {
    footer = (
      <div style={footerRow}>
        <button
          type="button"
          onClick={() => setPhase("configure")}
          style={secondaryBtn}
        >
          {t("skills.runDialog.tryAgain")}
        </button>
        <button type="button" onClick={onClose} style={primaryBtn}>
          {t("actions.close")}
        </button>
      </div>
    );
  }

  return (
    <WebDialog
      title={prettySkillName(skill.name)}
      subtitle={
        phase === "result"
          ? t(
              isTextSkill
                ? "skills.runDialog.resultTextSubtitle"
                : "skills.runDialog.resultSubtitle",
            )
          : t(
              isTextSkill
                ? "skills.runDialog.configureTextSubtitle"
                : "skills.runDialog.configureSubtitle",
            )
      }
      onClose={onClose}
      maxWidth={580}
      footer={footer}
    >
      {/* CONFIGURE */}
      {phase === "configure" ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 18,
            paddingBottom: 4,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <SkillTierBadge tier={skill.tier} />
            <span style={{ fontSize: 12, color: "var(--text-tertiary)" }}>
              {skill.authorName
                ? t("skills.by", { author: skill.authorName })
                : t("skills.byTabbio")}
            </span>
          </div>

          {skill.description ? (
            <section>
              <div style={sectionLabel}>{t("skills.runDialog.whatItDoes")}</div>
              <p style={descText} dir="auto">
                {skill.description}
              </p>
            </section>
          ) : null}

          <section
            style={{
              ...allowanceCard,
              borderColor: entitlementView.canRun
                ? "var(--input-border)"
                : "rgba(220, 38, 38, 0.28)",
              background: entitlementView.canRun
                ? "rgba(127, 127, 127, 0.08)"
                : "rgba(220, 38, 38, 0.08)",
            }}
            aria-live="polite"
          >
            <div style={{ display: "flex", minWidth: 0, flex: 1, gap: 10 }}>
              <MaterialSymbol
                name={
                  entitlementView.kind === "loading"
                    ? "hourglass_top"
                    : entitlementView.canRun
                      ? "check_circle"
                      : "info"
                }
                size={19}
                style={{ color: "var(--text-secondary)", marginTop: 1 }}
              />
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: "var(--text-primary)",
                  }}
                >
                  {entitlementView.title}
                </div>
                <div
                  style={{
                    marginTop: 2,
                    fontSize: 13,
                    lineHeight: "18px",
                    color: "var(--text-secondary)",
                  }}
                >
                  {entitlementView.summary}
                </div>
                {entitlementView.detail ? (
                  <div
                    style={{
                      marginTop: 2,
                      fontSize: 12,
                      color: "var(--text-tertiary)",
                    }}
                  >
                    {entitlementView.detail}
                  </div>
                ) : null}
                {errorMessage &&
                !entitlementView.canRun &&
                !entitlementView.detail?.includes(errorMessage) ? (
                  <div
                    style={{
                      marginTop: 4,
                      fontSize: 12,
                      lineHeight: "17px",
                      color: "var(--text-secondary)",
                    }}
                  >
                    {errorMessage}
                  </div>
                ) : null}
              </div>
            </div>
            {entitlementView.showUpgrade ? (
              <button
                type="button"
                onClick={() => openUpgrade(entitlementDecision)}
                style={allowanceUpgradeBtn}
              >
                {t("skills.runDialog.allowanceUpgrade")}
              </button>
            ) : null}
          </section>

          {detailLoading ? (
            /* Waiting on the detail (run spec + declarative inputs). */
            <div style={{ ...centeredState, padding: "28px 0" }}>
              <span style={spinner} />
            </div>
          ) : (
            <>
              {/* CV picker (cv / cv_jd / profile skills) */}
              {needsCvPicker ? (
                <section>
                  <div style={sectionLabel}>
                    {t("skills.runDialog.applyTo")}
                  </div>
                  {importing ? (
                    <div>
                      <ResumeImportComposer
                        title={t("seeker:cv.resumeImport.pageTitle")}
                        subtitle={t("seeker:cv.resumeImport.pageSubtitle")}
                        placeholder={t("seeker:cv.resumeImport.placeholder")}
                        parseFailedTitle={t(
                          "seeker:cv.resumeImport.failedTitle",
                        )}
                        parseFailedMessage={t(
                          "seeker:cv.resumeImport.failedMessage",
                        )}
                        onParsed={handleImportParsed}
                        secondaryActionLabel={t("actions.cancel")}
                        onSecondaryAction={() => setImporting(false)}
                        disabled={creatingCv}
                        showIntro={false}
                        skipEnrichment
                        analyticsContext="skill_run_web"
                      />
                      {creatingCv ? (
                        <p
                          style={{
                            margin: "12px 0 0",
                            fontSize: 14,
                            color: "var(--text-secondary)",
                          }}
                        >
                          {t("skills.runDialog.importing")}
                        </p>
                      ) : null}
                    </div>
                  ) : cvs != null && cvs.length === 0 ? (
                    <div style={noticeBox}>
                      <p
                        style={{
                          margin: 0,
                          fontSize: 14,
                          color: "var(--text-secondary)",
                        }}
                      >
                        {t("skills.runDialog.noCvBody")}
                      </p>
                      <div
                        style={{ display: "flex", gap: 8, flexWrap: "wrap" }}
                      >
                        <button
                          type="button"
                          onClick={() => {
                            router.navigate(buildMyTabbioRoute());
                            onClose();
                          }}
                          style={{ ...secondaryBtn, height: 40, flex: "none" }}
                        >
                          {t("skills.runDialog.createCv")}
                        </button>
                        <button
                          type="button"
                          onClick={() => setImporting(true)}
                          style={{ ...secondaryBtn, height: 40, flex: "none" }}
                        >
                          {t("skills.runDialog.importCv")}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      <div style={{ position: "relative" }}>
                        <select
                          value={selectedCvId ?? ""}
                          onChange={(e) =>
                            setSelectedCvId(e.target.value || null)
                          }
                          disabled={cvs == null}
                          dir={isRTL ? "rtl" : "ltr"}
                          style={selectStyle}
                        >
                          {cvs == null ? (
                            <option>{t("skills.runDialog.loadingCvs")}</option>
                          ) : (
                            cvs.map((cv) => (
                              <option key={cv.id} value={cv.id}>
                                {cv.title || t("skills.runDialog.untitledCv")}
                              </option>
                            ))
                          )}
                        </select>
                        <MaterialSymbol
                          name="expand_more"
                          size={20}
                          style={{
                            position: "absolute",
                            top: 15,
                            insetInlineEnd: 14,
                            color: "var(--text-tertiary)",
                            pointerEvents: "none",
                          }}
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => setImporting(true)}
                        style={{
                          ...secondaryBtn,
                          height: 40,
                          flex: "none",
                          alignSelf: "flex-start",
                        }}
                      >
                        <MaterialSymbol
                          name="upload"
                          size={17}
                          style={{ color: "var(--text-secondary)" }}
                        />
                        {t("skills.runDialog.importCvFromDevice")}
                      </button>
                    </div>
                  )}
                </section>
              ) : null}

              {/* Update vs new version (CV-editing skills only) */}
              {showApplyMode ? (
                <section>
                  <div style={sectionLabel}>
                    {t("skills.runDialog.applyMode")}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <ModeChip
                      active={applyMode === "update"}
                      icon="edit"
                      label={t("skills.runDialog.modeUpdate")}
                      onClick={() => setApplyMode("update")}
                    />
                    <ModeChip
                      active={applyMode === "new_version"}
                      icon="content_copy"
                      label={t("skills.runDialog.modeNewVersion")}
                      onClick={() => setApplyMode("new_version")}
                    />
                  </div>
                  {applyMode === "new_version" ? (
                    <input
                      value={versionName}
                      onChange={(e) => setVersionName(e.target.value)}
                      placeholder={t("skills.runDialog.versionNamePlaceholder")}
                      dir="auto"
                      style={{ ...inputStyle, marginTop: 10 }}
                    />
                  ) : (
                    <p style={reassure}>
                      <MaterialSymbol
                        name="info"
                        size={13}
                        style={{ verticalAlign: "-2px" }}
                      />{" "}
                      {t("skills.runDialog.updateNote")}
                    </p>
                  )}
                </section>
              ) : null}

              {/* tabbio/v2 declarative run-screen inputs */}
              {inputFields ? (
                <SkillDeclaredInputs
                  fields={inputFields}
                  values={fieldValues}
                  onChange={(id, value) =>
                    setFieldValues((prev) => ({ ...prev, [id]: value }))
                  }
                  isRTL={isRTL}
                />
              ) : null}

              {/* Job description */}
              {needsJd ? (
                <section>
                  <div style={sectionLabel}>
                    {t("skills.runDialog.jobLabel")}
                  </div>
                  <textarea
                    value={jobDescription}
                    onChange={(e) => setJobDescription(e.target.value)}
                    placeholder={t("skills.runDialog.jobPlaceholder")}
                    dir="auto"
                    rows={5}
                    style={textareaStyle}
                  />
                </section>
              ) : null}

              {/* Topic */}
              {needsTopic ? (
                <section>
                  <div style={sectionLabel}>
                    {t("skills.runDialog.topicLabel")}
                  </div>
                  <input
                    value={topic}
                    onChange={(e) => setTopic(e.target.value)}
                    placeholder={t("skills.runDialog.topicPlaceholder")}
                    dir="auto"
                    style={inputStyle}
                  />
                </section>
              ) : null}

              {/* Free text */}
              {needsFreeText ? (
                <section>
                  <div style={sectionLabel}>
                    {t("skills.runDialog.freeTextLabel")}
                  </div>
                  <textarea
                    value={freeText}
                    onChange={(e) => setFreeText(e.target.value)}
                    placeholder={t("skills.runDialog.freeTextPlaceholder")}
                    dir="auto"
                    rows={4}
                    style={textareaStyle}
                  />
                </section>
              ) : null}

              {(kind === "none" || kind === "profile") && !inputFields ? (
                <p style={reassure}>{t("skills.runDialog.profileNote")}</p>
              ) : null}
            </>
          )}
        </div>
      ) : null}

      {/* RUNNING */}
      {phase === "running" ? (
        <div style={centeredState}>
          <span style={spinner} />
          <span
            style={{
              fontSize: 15,
              color: "var(--text-primary)",
              fontWeight: 500,
            }}
          >
            {t(
              isTextSkill
                ? "skills.runDialog.runningText"
                : "skills.runDialog.running",
              { name: prettySkillName(skill.name) },
            )}
          </span>
          <span style={{ fontSize: 13, color: "var(--text-tertiary)" }}>
            {t("skills.runDialog.runningHint")}
          </span>
        </div>
      ) : null}

      {/* RESULT */}
      {phase === "result" ? (
        isTextSkill ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={appliedBanner}>
              <MaterialSymbol name="check_circle" size={18} />
              <span>{t("skills.runDialog.textReady")}</span>
            </div>
            <div style={textOutputCard} dir="auto">
              {outputText || t("skills.runDialog.noOutput")}
            </div>
          </div>
        ) : emptyCvResult ? (
          <div style={centeredState}>
            <MaterialSymbol
              name="check_circle"
              size={30}
              style={{ color: "var(--text-secondary)" }}
            />
            <span
              style={{
                fontSize: 15,
                color: "var(--text-primary)",
                fontWeight: 500,
              }}
            >
              {t("skills.runDialog.noChangesTitle")}
            </span>
            <span
              style={{
                fontSize: 13,
                color: "var(--text-tertiary)",
                textAlign: "center",
              }}
            >
              {t("skills.runDialog.noChanges")}
            </span>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={appliedBanner}>
              <MaterialSymbol name="check_circle" size={18} />
              <span dir="auto">
                {applyMode === "update"
                  ? t("skills.runDialog.updatedCv", {
                      cv: selectedCv?.title || t("skills.runDialog.yourCv"),
                    })
                  : t("skills.runDialog.appliedTo", {
                      cv:
                        versionName.trim() ||
                        selectedCv?.title ||
                        t("skills.runDialog.yourCv"),
                    })}
              </span>
            </div>
            <SkillDiffView diff={diff} />
          </div>
        )
      ) : null}

      {/* ERROR */}
      {phase === "error" ? (
        <div style={centeredState}>
          <MaterialSymbol
            name="error"
            size={28}
            style={{ color: "var(--text-tertiary)" }}
          />
          <p
            style={{
              margin: 0,
              fontSize: 14,
              color: "var(--text-secondary)",
              textAlign: "center",
            }}
          >
            {errorMessage || t("skills.runDialog.failed")}
          </p>
        </div>
      ) : null}
    </WebDialog>
  );
}

function ModeChip({
  active,
  icon,
  label,
  onClick,
}: {
  active: boolean;
  icon: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: 1,
        height: 44,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 7,
        borderRadius: 12,
        border: "1px solid var(--input-border)",
        background: active ? "rgba(0,0,0,0.06)" : "transparent",
        color: active ? "var(--text-primary)" : "var(--text-secondary)",
        fontSize: 14,
        fontWeight: active ? 500 : 400,
        fontFamily: "inherit",
        cursor: "pointer",
      }}
    >
      <MaterialSymbol name={icon} size={17} />
      {label}
    </button>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const footerRow: CSSProperties = { display: "flex", gap: 10, width: "100%" };
const primaryBtn: CSSProperties = {
  flex: 1,
  height: 46,
  borderRadius: 9999,
  border: 0,
  backgroundColor: "var(--text-primary)",
  color: "var(--bg)",
  fontSize: 15,
  fontWeight: 500,
  fontFamily: "inherit",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  cursor: "pointer",
  transition: "opacity 150ms",
};
const secondaryBtn: CSSProperties = {
  flex: 1,
  height: 46,
  borderRadius: 9999,
  border: "1px solid var(--input-border)",
  background: "transparent",
  color: "var(--text-primary)",
  paddingInline: 20,
  fontSize: 15,
  fontWeight: 500,
  fontFamily: "inherit",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  cursor: "pointer",
};
const sectionLabel: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  color: "var(--text-tertiary)",
  textTransform: "uppercase",
  letterSpacing: 0.3,
  marginBottom: 8,
};
const descText: CSSProperties = {
  margin: 0,
  fontSize: 14,
  lineHeight: "21px",
  color: "var(--text-secondary)",
};
const selectStyle: CSSProperties = {
  width: "100%",
  height: 52,
  borderRadius: 16,
  border: "1px solid var(--input-border)",
  background: "var(--bg)",
  color: "var(--text-primary)",
  fontSize: 15,
  fontFamily: "inherit",
  paddingInline: "16px 40px",
  appearance: "none",
  WebkitAppearance: "none",
  cursor: "pointer",
};
const inputStyle: CSSProperties = {
  width: "100%",
  height: 52,
  borderRadius: 16,
  border: "1px solid var(--input-border)",
  background: "var(--bg)",
  color: "var(--text-primary)",
  fontSize: 15,
  fontFamily: "inherit",
  paddingInline: 16,
  boxSizing: "border-box",
};
const textareaStyle: CSSProperties = {
  width: "100%",
  minHeight: 110,
  borderRadius: 16,
  border: "1px solid var(--input-border)",
  background: "var(--bg)",
  color: "var(--text-primary)",
  fontSize: 15,
  lineHeight: "22px",
  fontFamily: "inherit",
  padding: "14px 16px",
  boxSizing: "border-box",
  resize: "vertical",
};
const reassure: CSSProperties = {
  margin: "8px 2px 0",
  fontSize: 12,
  lineHeight: "17px",
  color: "var(--text-tertiary)",
};
const noticeBox: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 12,
  padding: 16,
  borderRadius: 16,
  background: "rgba(0,0,0,0.04)",
};
const allowanceCard: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  padding: "12px 14px",
  borderRadius: 14,
  border: "1px solid var(--input-border)",
};
const allowanceUpgradeBtn: CSSProperties = {
  flex: "none",
  height: 34,
  paddingInline: 14,
  borderRadius: 9999,
  border: "1px solid var(--input-border)",
  background: "var(--bg)",
  color: "var(--text-primary)",
  fontFamily: "inherit",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
};
const appliedBanner: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 14px",
  borderRadius: 14,
  background: "rgba(0,0,0,0.05)",
  color: "var(--text-primary)",
  fontSize: 13,
  fontWeight: 500,
};
const textOutputCard: CSSProperties = {
  padding: "16px 18px",
  borderRadius: 16,
  background: "rgba(0,0,0,0.04)",
  color: "var(--text-primary)",
  fontSize: 14,
  lineHeight: "22px",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  maxHeight: 380,
  overflowY: "auto",
};
const centeredState: CSSProperties = {
  padding: "44px 0",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 12,
  color: "var(--text-secondary)",
};
const spinner: CSSProperties = {
  width: 24,
  height: 24,
  border: "2px solid rgba(0,0,0,0.12)",
  borderTopColor: "var(--text-secondary)",
  borderRadius: "50%",
  animation: "spin 0.8s linear infinite",
};
